import { type ReactNode, useState } from "react";
import { AlertTriangle, ExternalLink, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { openGenieNotificationChannels } from "@/components/agent/details/ConversationReportsCard";
import { OverviewSectionCard } from "@/components/agent/overview/OverviewSectionCard";
import { CopyableEmail } from "@/components/common/CopyableEmail";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useUpdateAgent } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";
import {
  LISTEN_NUDGE_HOUR_OPTIONS,
  ListenAlertSettings,
  MAX_LISTEN_SMS_NUMBERS,
  resolveListenAlertSettings,
} from "@/types/agent/listen-alerts";

const E164_RE = /^\+[1-9]\d{6,14}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface ListeningAlertsCardProps {
  agent: Agent;
  brand?: string;
}

const SettingRow = ({
  title,
  description,
  control,
}: {
  title: string;
  description: string;
  control: ReactNode;
}) => (
  <div className="flex items-start justify-between gap-4 py-3 border-b border-gray-100 last:border-0">
    <div className="min-w-0">
      <p className="text-sm font-medium text-gray-900">{title}</p>
      <p className="text-xs text-gray-500 mt-0.5">{description}</p>
    </div>
    <div className="shrink-0">{control}</div>
  </div>
);

/**
 * How a Listener genie tells the team about the calls and forwarded email it
 * hears: an urgent alert (email, Sync channels, SMS), a daily brief of what
 * still needs attention, a reminder for what stays unanswered, and an
 * opt-in recap after every call. Replaces Conversation Reports on listeners.
 */
export const ListeningAlertsCard = ({ agent, brand = "#4E9CFF" }: ListeningAlertsCardProps) => {
  const saved = resolveListenAlertSettings(agent.listen_alerts);
  const [isEditing, setIsEditing] = useState(false);
  const [settings, setSettings] = useState<ListenAlertSettings>(saved);
  const [emails, setEmails] = useState<string[]>(agent.report_recipients ?? []);
  const updateAgent = useUpdateAgent();

  const listenedInboxes = agent.mailbox?.listened_inboxes ?? [];
  const recipients = isEditing ? emails : (agent.report_recipients ?? []);
  const listenedRecipients = recipients.filter((email) => listenedInboxes.includes(email.trim().toLowerCase()));
  const hasPhone = Boolean(agent.phone?.number);
  const connectedChannels = (agent.connected_channels ?? []).filter((c) => c.status === "connected");

  const update = (patch: Partial<ListenAlertSettings>) => setSettings((current) => ({ ...current, ...patch }));

  const handleEdit = () => {
    setSettings(resolveListenAlertSettings(agent.listen_alerts));
    setEmails(agent.report_recipients ?? []);
    setIsEditing(true);
  };

  const handleSave = async () => {
    const smsNumbers = settings.sms_numbers.map((n) => n.replace(/[\s()-]/g, "")).filter(Boolean);
    if (smsNumbers.some((n) => !E164_RE.test(n))) {
      toast.error("Enter phone numbers with the country code, like +64211234567");
      return;
    }
    const cleanEmails = emails.map((e) => e.trim()).filter(Boolean);
    if (cleanEmails.some((e) => !EMAIL_RE.test(e))) {
      toast.error("Enter valid email addresses");
      return;
    }
    await updateAgent.mutateAsync({
      agent,
      updateData: {
        listen_alerts: { ...settings, sms_numbers: smsNumbers },
        // Persisted to notification_recipients by handle-agent/update.ts.
        settings: { ...agent.settings, support_team_emails: cleanEmails },
      },
    });
    toast.success("Listening alerts saved");
    setIsEditing(false);
  };

  const describeUrgent = () => {
    const ways = ["email", ...connectedChannels.map((c) => c.type)];
    if (saved.sms_numbers.length > 0) ways.push(`SMS to ${saved.sms_numbers.length} ${saved.sms_numbers.length === 1 ? "phone" : "phones"}`);
    return `As it happens, by ${ways.join(", ")}`;
  };

  const view = (
    <div className="space-y-5">
      <div>
        <SettingRow
          title="Urgent alerts"
          description={saved.urgent_alerts ? describeUrgent() : "Off"}
          control={<span className="text-xs font-semibold" style={{ color: saved.urgent_alerts ? brand : "#9CA3AF" }}>{saved.urgent_alerts ? "On" : "Off"}</span>}
        />
        <SettingRow
          title="Daily brief"
          description={
            saved.daily_brief
              ? "Every morning: what still needs attention, and one line for everything else. Skipped on quiet days."
              : "Off"
          }
          control={<span className="text-xs font-semibold" style={{ color: saved.daily_brief ? brand : "#9CA3AF" }}>{saved.daily_brief ? "On" : "Off"}</span>}
        />
        <SettingRow
          title="Reminder"
          description={saved.nudge_hours ? `When something needs attention and nobody handles it within ${saved.nudge_hours} hours` : "Off"}
          control={<span className="text-xs font-semibold" style={{ color: saved.nudge_hours ? brand : "#9CA3AF" }}>{saved.nudge_hours ? `${saved.nudge_hours}h` : "Off"}</span>}
        />
        <SettingRow
          title="Recap after every call"
          description={saved.call_recaps ? "A summary email after each phone call" : "Off: calls reach you through alerts and the daily brief"}
          control={<span className="text-xs font-semibold" style={{ color: saved.call_recaps ? brand : "#9CA3AF" }}>{saved.call_recaps ? "On" : "Off"}</span>}
        />
      </div>

      <div>
        <p className="text-sm font-semibold text-gray-700 mb-1">Who gets them</p>
        {recipients.length > 0 ? (
          <div className="space-y-1">
            {recipients.map((email) => (
              <div key={email} className="text-sm text-gray-600 rounded px-3 py-1.5 border border-gray-200">
                <CopyableEmail email={email} />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-500">Your account email. Add team members with Edit.</p>
        )}
      </div>
    </div>
  );

  const edit = (
    <div className="space-y-5">
      <div>
        <SettingRow
          title="Urgent alerts"
          description="Email, your Sync channels and SMS when a call or email needs someone now"
          control={<Switch checked={settings.urgent_alerts} onCheckedChange={(v) => update({ urgent_alerts: v })} />}
        />
        <SettingRow
          title="Daily brief"
          description="Every morning, only when something needs attention"
          control={<Switch checked={settings.daily_brief} onCheckedChange={(v) => update({ daily_brief: v })} />}
        />
        <SettingRow
          title="Reminder"
          description="When something that needs attention stays unhandled"
          control={
            <Select
              value={settings.nudge_hours ? String(settings.nudge_hours) : "off"}
              onValueChange={(v) => update({ nudge_hours: v === "off" ? null : Number(v) })}
            >
              <SelectTrigger className="w-32 text-base sm:text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off</SelectItem>
                {LISTEN_NUDGE_HOUR_OPTIONS.map((hours) => (
                  <SelectItem key={hours} value={String(hours)}>
                    After {hours}h
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <SettingRow
          title="Recap after every call"
          description="Also email a summary after each phone call"
          control={<Switch checked={settings.call_recaps} onCheckedChange={(v) => update({ call_recaps: v })} />}
        />
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">SMS for urgent alerts</p>
        <p className="text-xs text-gray-500 mb-2">
          {hasPhone
            ? `Texted from ${agent.phone?.number}. Up to ${MAX_LISTEN_SMS_NUMBERS} numbers, with the country code.`
            : "Texts are sent from your genie's phone number. Add one in Phone Listening first."}
        </p>
        <div className="space-y-2">
          {settings.sms_numbers.map((number, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="tel"
                value={number}
                onChange={(e) => {
                  const next = [...settings.sms_numbers];
                  next[index] = e.target.value;
                  update({ sms_numbers: next });
                }}
                placeholder="+64211234567"
                className="flex-1 min-w-0 max-w-xs rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm"
              />
              <button
                type="button"
                onClick={() => update({ sms_numbers: settings.sms_numbers.filter((_, i) => i !== index) })}
                className="flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                aria-label="Remove number"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          {settings.sms_numbers.length < MAX_LISTEN_SMS_NUMBERS && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={!hasPhone}
              onClick={() => update({ sms_numbers: [...settings.sms_numbers, ""] })}
            >
              + Add phone
            </Button>
          )}
        </div>
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">Who gets them</p>
        <p className="text-xs text-gray-500 mb-2">Leave empty to use your account email.</p>
        <div className="space-y-2">
          {emails.map((email, index) => (
            <div key={index} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  const next = [...emails];
                  next[index] = e.target.value;
                  setEmails(next);
                }}
                placeholder="you@example.com"
                className="flex-1 min-w-0 max-w-sm rounded-md border border-gray-300 px-3 py-2 text-base sm:text-sm"
              />
              <button
                type="button"
                onClick={() => setEmails(emails.filter((_, i) => i !== index))}
                className="flex min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 items-center justify-center px-3 py-2 text-red-600 hover:bg-red-50 rounded-md border border-red-200"
                aria-label="Remove email"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <Button type="button" variant="outline" size="sm" onClick={() => setEmails([...emails, ""])}>
            + Add email
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <OverviewSectionCard
      title="Listening Alerts"
      subtitle="Only what needs your team: urgent alerts and a daily brief"
      genieIcon="bell"
      brand={brand}
      secondaryColor={agent.branding?.secondaryColor}
      gradientEnabled={agent.branding?.gradientEnabled}
      gradientAngle={agent.branding?.gradientAngle}
      onEdit={isEditing ? undefined : handleEdit}
      onSave={isEditing ? handleSave : undefined}
      onCancel={isEditing ? () => setIsEditing(false) : undefined}
      isSaving={updateAgent.isPending}
    >
      <div className="space-y-5">
        {isEditing ? edit : view}

        {listenedRecipients.length > 0 && (
          <div className="flex items-start gap-2 rounded-lg bg-genie-accent/10 px-3 py-2.5">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-genie-accent" />
            <p className="text-xs text-gray-700">
              {listenedRecipients.join(", ")} {listenedRecipients.length === 1 ? "is" : "are"} the inbox your genie
              listens to. Alerts sent there get lost among the mail it filters, so a personal or team address works
              better.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4">
          <p className="text-xs text-gray-500">
            {connectedChannels.length > 0
              ? `Also sent to ${connectedChannels.map((c) => c.type).join(", ")}`
              : "Send alerts to Slack, Teams, WhatsApp or Telegram too"}
          </p>
          <button
            type="button"
            onClick={() => openGenieNotificationChannels(agent.id)}
            className="inline-flex shrink-0 items-center gap-1 text-xs font-medium hover:underline"
            style={{ color: brand }}
          >
            Edit in Help Genie Sync
            <ExternalLink className="w-3 h-3" />
          </button>
        </div>
      </div>
    </OverviewSectionCard>
  );
};
